import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const totalStock = (product.sizes || []).reduce((n, s) => n + s.stock, 0);
  const soldOut = totalStock <= 0;

  const quickAdd = (e, size) => {
    e.preventDefault();
    addItem(product, size);
    toast.success(`${product.name} — ${size} added to cart`);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.06 }}
      className="group"
    >
      <Link to={`/product/${product.id}`} data-testid={`product-card-${product.id}`} className="block">
        <div className="relative overflow-hidden border border-[#E4E4DE] bg-[#EFEFEA] group-hover:border-[#C8FF1E]/60 transition-colors duration-300">
          <img
            src={product.main_image}
            alt={product.name}
            loading="lazy"
            className="w-full aspect-[3/4] object-cover transition-all duration-700 group-hover:scale-105 group-hover:opacity-0"
          />
          <img
            src={product.model_image || product.main_image}
            alt={`${product.name} on model`}
            loading="lazy"
            className="absolute inset-0 w-full aspect-[3/4] object-cover opacity-0 scale-105 transition-all duration-700 group-hover:opacity-100 group-hover:scale-100"
          />
          {product.tag && (
            <span className="absolute top-3 left-3 bg-[#C8FF1E] text-black font-mono text-[10px] font-bold uppercase tracking-widest px-2 py-1">
              {product.tag}
            </span>
          )}
          <span className="absolute top-3 right-3 bg-black/70 backdrop-blur px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
            {product.gsm}
          </span>
          {soldOut && (
            <span
              data-testid={`sold-out-${product.id}`}
              className="absolute inset-0 flex items-center justify-center bg-black/60 font-display text-2xl font-extrabold uppercase tracking-tighter text-red-500"
            >
              Sold out
            </span>
          )}
          {!soldOut && (
            <div className="absolute bottom-0 inset-x-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-[#F7F7F2]/90 backdrop-blur border-t border-[#E4E4DE] px-3 py-2.5 hidden sm:flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Quick add</span>
              <span className="flex gap-1.5">
                {(product.sizes || []).map((s) => (
                  <button
                    key={s.size}
                    data-testid={`quick-add-${product.id}-${s.size}`}
                    disabled={s.stock <= 0}
                    onClick={(e) => quickAdd(e, s.size)}
                    className="h-8 min-w-8 px-1 border border-[#E4E4DE] font-mono text-xs text-zinc-700 hover:bg-[#C8FF1E] hover:text-black hover:border-[#C8FF1E] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-zinc-700 transition-colors"
                  >
                    {s.size}
                  </button>
                ))}
              </span>
            </div>
          )}
        </div>
        <div className="mt-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-bold tracking-tight uppercase leading-tight">
              {product.name}
            </h3>
            <p className="mt-1 font-mono text-xs uppercase tracking-wider text-zinc-500">
              {product.color} · {product.category}
            </p>
          </div>
          <p data-testid={`product-price-${product.id}`} className="font-mono text-sm font-bold text-[#4D7C0F]">
            ${product.price}
          </p>
        </div>
      </Link>
    </motion.article>
  );
};
